"use client"

import { useState, useEffect } from "react"
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Loader2 } from "lucide-react"
import { useToast } from "@/components/ui/use-toast"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { supabase } from "@/utils/supabase/client"
import { useAuth } from "@/contexts/auth-context"
import { format } from "date-fns"
import { Expense, ExpenseCategory } from "@/types"

type ExpenseWithCategory = Expense & {
    expense_categories: {
        name: string;
    } | null;
};

export function ExpenseAnalytics() {
  const [categoryWiseExpense, setCategoryWiseExpense] = useState<{ name: string, value: number }[]>([])
  const [monthlyExpense, setMonthlyExpense] = useState<{ name: string, total: number }[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const { toast } = useToast()
  const { currentOrganization } = useAuth()

  useEffect(() => {
    const fetchExpenses = async () => {
      if (!currentOrganization) return
      setIsLoading(true)

      try {
        const { data: expenses, error } = await supabase
          .from("expenses")
          .select(`
            amount,
            expense_date,
            expense_categories ( name )
          `)
          .eq("organization_id", currentOrganization.id)

        if (error) throw error

        const typedExpenses = expenses as unknown as ExpenseWithCategory[]

        const categoryData = typedExpenses.reduce((acc, e) => {
          const categoryName = e.expense_categories?.name || 'Uncategorized'
          acc[categoryName] = (acc[categoryName] || 0) + e.amount
          return acc
        }, {} as Record<string, number>)

        setCategoryWiseExpense(Object.entries(categoryData).map(([name, value]) => ({ name, value })))

        const monthlyData = typedExpenses.reduce((acc, e) => {
          const month = format(new Date(e.expense_date), 'MMM yyyy')
          acc[month] = (acc[month] || 0) + e.amount
          return acc
        }, {} as Record<string, number>)
        
        const sortedMonths = Object.keys(monthlyData).sort((a,b) => new Date(a).getTime() - new Date(b).getTime())
        setMonthlyExpense(sortedMonths.map(month => ({ name: month, total: monthlyData[month] })))

      } catch (error: any) {
        toast({
          title: "Error fetching expense data",
          description: error.message,
          variant: "destructive",
        })
      } finally {
        setIsLoading(false)
      }
    }

    fetchExpenses()
  }, [currentOrganization, toast])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6 mt-6">
      <div className="grid gap-6 md:grid-cols-2">
        <Card className="md:col-span-2">
            <CardHeader><CardTitle>Monthly Expense Trend</CardTitle></CardHeader>
            <CardContent>
                 <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={monthlyExpense}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" stroke="#888888" fontSize={12} />
                        <YAxis stroke="#888888" fontSize={12} tickFormatter={(value) => `Rs.${value/1000}k`} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="total" fill="#ff7300" name="Monthly Expense" />
                    </BarChart>
                </ResponsiveContainer>
            </CardContent>
        </Card>
        <Card className="md:col-span-2">
            <CardHeader><CardTitle>Category Comparison</CardTitle></CardHeader>
            <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={categoryWiseExpense} layout="vertical">
                         <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" stroke="#888888" fontSize={12} tickFormatter={(value) => `Rs.${value/1000}k`} />
                        <YAxis type="category" dataKey="name" stroke="#888888" fontSize={12} width={100} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="value" fill="#387908" name="Total Spent" />
                    </BarChart>
                </ResponsiveContainer>
            </CardContent>
        </Card>
      </div>
    </div>
  )
} 